/*
 * Ryan O'Dowd
 * 2025-07-09
 * © Copyright 2025 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import React, {
  useState,
} from 'react';
import Checkbox from '@components/common/Checkbox';
import IconButton from '@components/common/IconButton';
import Table from '@components/common/Table';
import Text from '@components/common/Text';
import Tooltip from '@components/common/Tooltip';
import {
  View,
} from 'react-native';
import {
  format as dateFnsFormat,
} from 'date-fns';
import {
  decideOnApiSyncChanges,
} from '@utils/store/actions';
import {
  diffChars,
} from 'diff';
import getStyles from './styles';
import hooks from '@hooks';

const TranslationManager = (props: TranslationManagerProps) => {
  const styles = hooks.useStyles(getStyles);
  const dispatch = hooks.useTypedDispatch();

  const [_selectedMetadataIds, setSelectedMetadataIds] = useState(new Set());

  const _formatDiff = (current, next) => {
    const diffText = diffChars(`${current}`, `${next}`);
    const diffPieces = diffText.map((part, index) => {
      return (
        <Text
          key={index}
          style={[styles.diffChar, part.added && styles.addedChar, part.removed && styles.removedChar]}
        >
          {part.value}
        </Text>
      );
    });
    return (
      <View style={styles.diffWrapper}>
        <Text style={styles.currentText}>{current}</Text>
        <Text style={styles.newText}>{next}</Text>
        <Text style={styles.diffText}>{diffPieces}</Text>
      </View>
    );
  };

  const getAcceptRejectButtons = (metadataIds) => {
    return (
      <View style={styles.actions}>
        <IconButton
          icon='check'
          iconColor={styles.colors.accentDark}
          onPress={() => dispatch(decideOnApiSyncChanges(metadataIds, 'accepted', props.fetchApiSyncStagingMetadataSuccessCallback))}
        />
        <IconButton
          icon='close'
          iconColor={styles.colors.alert}
          onPress={() => dispatch(decideOnApiSyncChanges(metadataIds, 'rejected', props.fetchApiSyncStagingMetadataSuccessCallback))}
        />
      </View>
    );
  };

  const tableRows = props.diffs.map((diff) => {
    return [
      {
        id: `${diff.metadata_id}_checkbox`,
        content: (
          <Checkbox
            isChecked={_selectedMetadataIds.has(diff.metadata_id)}
            onPress={() => {
              setSelectedMetadataIds((curr) => {
                const newSet = new Set(curr);
                if (newSet.has(diff.metadata_id)) {
                  newSet.delete(diff.metadata_id);
                } else {
                  newSet.add(diff.metadata_id);
                }

                return newSet;
              });
            }}
          />
        ),
        sortValue: diff.item?.toLocaleLowerCase(),
      }, // @TODO: also a type? (verse text, footnate, metadata, etc.?)
      {
        id: `${diff.metadata_id}_item`,
        content: <Text>{diff.item}</Text>,
        sortValue: diff.item?.toLocaleLowerCase(),
      }, // @TODO: also a type? (verse text, footnate, metadata, etc.?)
      {
        id: `${diff.metadata_id}_diff`,
        content: props.skipDiff ? diff.next : _formatDiff(diff.current, diff.next),
        sortValue: diff.next,
      },
      {
        id: `${diff.metadata_id}_type`,
        content: <Text>{diff.change_type}</Text>,
        sortValue: diff.change_type, // @TODO: deleted types NEED to be accepted/rejected first (e.g. a footnote changes letters due to a new one being added...this will resutl in 2 "new" footnotes and 1 "deleted" footnote in staging)
      },
      {
        id: `${diff.metadata_id}_time`,
        content: <Tooltip title={dateFnsFormat(new Date(diff.synced_at), 'h:mm a')}><Text>{dateFnsFormat(new Date(diff.synced_at), 'MMM d, yyyy')}</Text></Tooltip>,
        sortValue: diff.synced_at, // @TODO: make this a datetime sort
      },
      {
        id: `${diff.metadata_id}_actions`,
        content: getAcceptRejectButtons([diff.metadata_id]),
        sortValue: 0, // @TODO: really doesn't need to be sortable
      },
      {
        id: `${diff.metadata_id}_batch_id`,
        content: <Text>{diff.batch_id}</Text>,
        sortValue: diff.batch_id,
      },
    ];
  });

  let checkAllStatus = null;
  if (_selectedMetadataIds.size === props.diffs.length) {
    checkAllStatus = true;
  } else if (_selectedMetadataIds.size === 0) {
    checkAllStatus = false;
  }

  return (
    <Table
      tableTitle={props.type}
      tableSubtitle={`${props.diffs.length} for review`}
      open={false}
      hasBadge={true}
      badgeText={`${props.diffs.length}`}
      headerRowData={[
        {
          width: 90,
          id: 'checkbox', // @TODO: would be nice for this to be a prop instead of rebuilding it each time i want a checkbox in my table
          content: (
            <Checkbox
              isChecked={checkAllStatus}
              onPress={() => {
                if (_selectedMetadataIds.size > 0 && _selectedMetadataIds.size < props.diffs.length) {
                  setSelectedMetadataIds(new Set());
                } else if (_selectedMetadataIds.size === props.diffs.length) {
                  setSelectedMetadataIds(new Set());
                } else {
                  setSelectedMetadataIds(new Set(props.diffs.map((d) => d.metadata_id)));
                }
              }}
            />
          ),
        },
        {width: 160, id: 'item', content: 'Item'},
        {width: 240, id: 'diff', content: 'Diff'},
        {width: 90, id: 'change_type', content: 'Type'},
        {width: 100, id: 'synced_at', content: 'Synced at'},
        {width: 90, id: 'actions', content: 'Actions', rightAlign: true},
        {width: 90, id: 'batch_id', content: 'Batch ID', rightAlign: true},
      ]}
      isSortable={true}
      orderByIndex={1}
      rowData={tableRows || []}// @TODO: shouldn't be clickable (or if they are, make individually updatable)// @TODO: shouldn't need || []
      showRowsPerPage={true}
      filtersJsx={_selectedMetadataIds.size > 0 && (
        <View style={styles.bulkActionsWrapper}>{/* @TODO: make this prettier */}
          <Text>Bulk actions</Text>
          {getAcceptRejectButtons(Array.from(_selectedMetadataIds))}
        </View>
      )}
    />
  );
};

class TranslationManagerProps {
  type: 'Translation metadata' | 'Verse texts' | 'Footnotes' | 'Book names' | 'Miscellaneous notes';
  diffs: any;
  fetchApiSyncStagingMetadataSuccessCallback: () => void;

  skipDiff?: boolean;
};

export default TranslationManager;
